import { Component, Element, h, Listen, Method, Prop, State, Watch } from '@stencil/core';

@Component({
  tag: 'dc-ui-anchor-bar',
  styleUrl: 'dc-ui-anchor-bar.scss',
  shadow: true,
})
export class DcUiAnchorBar {
  @Element() el: HTMLElement;
  @Prop() scrollOffset: number = 0;
  @Prop() headerElementName: string = 'dc-ui-header';

  @State() isAnchorBarSticky: boolean = false;
  @State() isHeaderShown: boolean = false;
  @State() hasOverflow: boolean = false;
  @State() isAtStart: boolean = true;
  @State() isAtEnd: boolean = false;

  // DOM Elements
  private header: HTMLElement;
  private anchorBarContainer: HTMLElement;
  private anchorItems: HTMLElement[] = [];
  private sections: HTMLElement[] = [];

  // Observers
  private lastSectionObserver: IntersectionObserver;

  // State tracking
  private currentIndex: number = -1;
  private lastScrollPosition: number = window.scrollY;
  private isScrollingUp: boolean = false;
  private lastKnownScrollPosition: number = 0;
  private isShadowRoot: boolean = false;
  private initialYPosition: number = 0;

  private sectionsMap: Map<string, HTMLElement> = new Map();

  // Layout constants
  private readonly LAYOUT = {
    INLINE_PADDING: 15,
    OVERFLOW_BUTTON_WIDTH: 32,
    OVERFLOW_BUTTON_GAP: 12,
    ITEM_GAP: 12,
  } as const;

  @Listen('dcHeaderShown', { target: 'window' })
  handleHeaderShown() {
    this.isHeaderShown = true;
    this.el.style.setProperty('--header-offset-height', `${this.getHeaderHeight()}px`);
  }

  @Listen('dcHeaderHidden', { target: 'window' })
  handleHeaderHidden() {
    this.isHeaderShown = false;
    this.el.style.setProperty('--header-offset-height', `0px`);
  }

  @Watch('currentIndex')
  handleCurrentIndexChange() {
    this.updateButtonStates();
  }

  // Methods
  @Method()
  async goToSectionIndex(index: number) {
    this.currentIndex = index;
    this.toggleActiveAnchor(index);
  }

  // Lifecycle Methods
  componentDidLoad() {
    const rootNode = this.el.getRootNode() as Document | ShadowRoot;
    this.isShadowRoot = rootNode instanceof ShadowRoot;

    this.initialYPosition = this.el.getBoundingClientRect().top;

    this.initializeElements();
    this.setupObserver();
    this.setupEventListeners();
    this.checkForOverflow();
  }

  disconnectedCallback() {
    this.cleanupObserver();
    this.cleanupEventListeners();
  }

  private setupEventListeners() {
    this.setupClickHandlers();
    window.addEventListener('scroll', this.handleScroll, { passive: true });
    window.addEventListener('scroll', this.setScrollDirection, { passive: true });
    window.addEventListener('resize', this.checkForOverflow);
  }

  private cleanupEventListeners() {
    window.removeEventListener('scroll', this.handleScroll);
    window.removeEventListener('scroll', this.setScrollDirection);
    window.removeEventListener('resize', this.checkForOverflow);

    // Add cleanup for anchor item click listeners
    this.anchorItems.forEach((item) => {
      item.removeEventListener('click', this.handleAnchorClick);
    });
  }

  private cleanupObserver() {
    this.lastSectionObserver?.disconnect();
  }

  // Initialize Elements
  private initializeElements() {
    // Get header reference considering shadow DOM
    this.header = document.querySelector(this.headerElementName) || null;

    if (this.headerElementName !== 'dc-ui-header') {
      this.updateStickyOffset(true);
    }

    // Get anchor items
    this.anchorItems = Array.from(this.el.children).filter(
      (child) => child.tagName.toLowerCase() === 'dc-ui-anchor-bar-item'
    ) as HTMLElement[];

    if (this.isShadowRoot) {
      this.sections = this.anchorItems.map((item) => {
        const root = item.getRootNode() as ShadowRoot;
        const section = root.host.shadowRoot.querySelector(item.getAttribute('href'));
        this.sectionsMap.set(item.getAttribute('href'), section as HTMLElement);
        return section as HTMLElement;
      });
    } else {
      this.sections = this.anchorItems
        .map((item) => {
          const href = item.getAttribute('href');
          const section = document.querySelector(href);
          if (section) {
            this.sectionsMap.set(href, section as HTMLElement);
          }
          return section;
        })
        .filter(Boolean) as HTMLElement[];
    }

    this.updateButtonStates();
  }

  private setupObserver() {
    // Modified last section observer
    const lastSection = this.sections[this.sections.length - 1]?.nextElementSibling;
    if (lastSection) {
      this.lastSectionObserver = new IntersectionObserver(
        (entries) => {
          const lastSectionEntry = entries[0];
          const rect = lastSectionEntry.boundingClientRect;
          const anchorBarHeight = this.getAnchorBarHeight();

          if (!lastSectionEntry.isIntersecting && rect.bottom <= anchorBarHeight) {
            // Past the bottom of all sections
            this.isAnchorBarSticky = false;
          } else if (lastSectionEntry.isIntersecting && this.isScrollingUp) {
            // When scrolling back up into sections, let the anchorBarStickyObserver handle it
            const anchorBarRect = this.el.getBoundingClientRect();
            if (anchorBarRect.top <= 0) {
              this.isAnchorBarSticky = true;
              this.updateStickyOffset(true);
            }
          }
        },
        {
          threshold: [0],
          rootMargin: '0px 0px 0px 0px',
        }
      );
      this.lastSectionObserver.observe(lastSection);
    }
  }

  // Click Handlers
  private setupClickHandlers() {
    this.anchorItems.forEach((item) => {
      item.addEventListener('click', this.handleAnchorClick);
    });
  }

  // Move click handler to class method for proper cleanup
  private handleAnchorClick = (e: Event) => {
    e.preventDefault();
    const item = e.currentTarget as HTMLElement;
    const href = item.getAttribute('href');
    this.scrollToSection(href);
  };

  // Helper Methods
  private getHeaderHeight(): number {
    if (!this.header) return 0;
    return this.header.getBoundingClientRect().height;
  }

  private getAnchorBarHeight(): number {
    return this.anchorBarContainer?.getBoundingClientRect().height ?? 0;
  }

  private updateStickyOffset(zero: boolean = false) {
    this.el.style.setProperty('--header-offset-height', `${zero ? this.getHeaderHeight() : 0}px`);
  }

  private scrollToSection(href: string) {
    const section = this.sectionsMap.get(href);
    if (!section) return;

    const scrollDirection = this.getScrollDirection(section as HTMLElement);

    const headerHeight = scrollDirection === 'up' ? this.getHeaderHeight() : 0;
    const anchorBarHeight = this.getAnchorBarHeight();
    const safeZoneOffset = this.scrollOffset;
    const totalOffset =
      headerHeight +
      anchorBarHeight +
      (this.isAnchorBarSticky ? 0 : anchorBarHeight) +
      safeZoneOffset;

    // Set a CSS custom property for scroll-margin-top
    (section as HTMLElement).style.scrollMarginTop = `${totalOffset}px`;

    section.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });

    // remove scroll-margin-top
    (section as HTMLElement).style.removeProperty('scroll-margin-top');

    // Update URL without scroll
    history.pushState(null, '', href);
  }

  private setScrollDirection = (): void => {
    const currentScrollPosition = window.scrollY;
    const wasScrollingUp = this.isScrollingUp;
    this.isScrollingUp = currentScrollPosition < this.lastScrollPosition;
    this.lastScrollPosition = currentScrollPosition;

    // Check if scroll direction changed and update offset if sticky
    if (wasScrollingUp !== this.isScrollingUp && this.isAnchorBarSticky) {
      this.updateStickyOffset(this.isScrollingUp);
    }
  };

  private handleScroll = (): void => {
    this.lastKnownScrollPosition = window.scrollY;

    requestAnimationFrame(() => {
      setTimeout(() => {
        if (this.lastKnownScrollPosition === window.scrollY) {
          this.updateActiveSection();
        }
      }, 150);
    });

    // update sticky state
    const topEdge = window.scrollY + (this.isHeaderShown ? this.getHeaderHeight() : 0);
    const shouldBeSticky = this.initialYPosition < topEdge;
    if (shouldBeSticky !== this.isAnchorBarSticky) {
      this.isAnchorBarSticky = shouldBeSticky;
      if (this.isAnchorBarSticky) {
        this.updateStickyOffset(this.headerElementName !== 'dc-ui-header');
      }
    }
  };

  private updateActiveSection = (): void => {
    // Skip updating active section if not sticky
    if (!this.isAnchorBarSticky) {
      this.currentIndex = -1;
      this.toggleActiveAnchor(-1);
      return;
    }

    const activeSection = this.getActiveSection();
    this.currentIndex = activeSection;
    this.toggleActiveAnchor(activeSection);
  };

  private getActiveSection = (): number => {
    const headerOffset = this.isHeaderShown
      ? this.getHeaderHeight() + this.getAnchorBarHeight()
      : this.getAnchorBarHeight();
    const currentPosition = window.scrollY + headerOffset;
    let activeSection = null;

    for (let i = this.sections.length - 1; i >= 0 && activeSection === null; i--) {
      if (currentPosition >= this.getElementPosition(i)) {
        activeSection = i;
      }
    }
    return activeSection;
  };

  private getElementPosition = (index: number): number => {
    const section = this.sections[index];
    const headerOffset = this.isScrollingUp && this.isHeaderShown ? this.getHeaderHeight() : 0;
    const anchorBarOffset = this.getAnchorBarHeight();
    // Adjust the position calculation to account for header and anchor bar visibility
    return section.getBoundingClientRect().top + window.scrollY - headerOffset - anchorBarOffset;
  };

  private toggleActiveAnchor = (index: number): void => {
    const anchorElements = this.anchorItems;
    for (let i = 0; i < anchorElements.length; i += 1) {
      // Always remove attributes if not sticky or if not the active index
      if (!this.isAnchorBarSticky || i !== index) {
        anchorElements[i].removeAttribute('selected');
        anchorElements[i].removeAttribute('aria-current');
        continue;
      }

      // Only set attributes if sticky and is active index
      anchorElements[i].setAttribute('selected', '');
      anchorElements[i].setAttribute('aria-current', 'location');

      if (this.hasOverflow && this.isAnchorBarSticky) {
        anchorElements[i].scrollIntoView({
          behavior: 'smooth',
          inline: 'center',
        });
      }
    }

    // Update button states after toggling active anchor
    this.updateButtonStates();
  };

  private getScrollDirection(targetSection: HTMLElement): 'up' | 'down' {
    const currentSection = this.currentIndex !== -1 ? this.sections[this.currentIndex] : null;
    if (!currentSection) {
      return targetSection.getBoundingClientRect().top > window.innerHeight / 2 ? 'down' : 'up';
    }
    return targetSection.getBoundingClientRect().top < currentSection.getBoundingClientRect().top
      ? 'up'
      : 'down';
  }

  private checkForOverflow = () => {
    if (!this.anchorBarContainer) return;

    const { width: containerWidth } = this.anchorBarContainer.getBoundingClientRect();
    const { INLINE_PADDING, OVERFLOW_BUTTON_WIDTH, OVERFLOW_BUTTON_GAP } = this.LAYOUT;

    const availableWidth =
      containerWidth - INLINE_PADDING * 2 - OVERFLOW_BUTTON_WIDTH * 2 - OVERFLOW_BUTTON_GAP * 2;

    const itemsWidth = this.anchorItems.reduce(
      (acc, item) => acc + item.getBoundingClientRect().width + this.LAYOUT.ITEM_GAP,
      -this.LAYOUT.ITEM_GAP // Subtract one gap since we don't need it after the last item
    );

    this.hasOverflow = itemsWidth > availableWidth;
  };

  private handleOverflowButtonClick(direction: 'left' | 'right') {
    const nextIndex =
      direction === 'left'
        ? Math.max(0, this.currentIndex - 1)
        : Math.min(this.anchorItems.length - 1, this.currentIndex + 1);

    if (nextIndex !== this.currentIndex) {
      this.scrollToSection(this.anchorItems[nextIndex].getAttribute('href'));
    }
  }

  private updateButtonStates() {
    // Find the active anchor item (one with selected attribute)
    const activeIndex = this.anchorItems.findIndex((item) => item.hasAttribute('selected'));

    // If no item is selected, disable left button and enable right button
    if (activeIndex === -1) {
      this.isAtStart = true;
      this.isAtEnd = false;
      return;
    }

    this.isAtStart = activeIndex === 0;
    this.isAtEnd = activeIndex === this.anchorItems.length - 1;
  }

  // Render
  render() {
    const overflowButtonLeft = (
      <dc-ui-button
        icon="dc_backward_left"
        variant="link"
        size="small"
        hideLabel={true}
        disabled={this.isAtStart}
        onClick={() => this.handleOverflowButtonClick('left')}>
        Go to previous section
      </dc-ui-button>
    );

    const overflowButtonRight = (
      <dc-ui-button
        icon="dc_forward_right"
        variant="link"
        size="small"
        hideLabel={true}
        disabled={this.isAtEnd}
        onClick={() => this.handleOverflowButtonClick('right')}>
        Go to next section
      </dc-ui-button>
    );

    return (
      <div
        class={{
          'dc-ui-anchor-bar': true,
          'dc-ui-anchor-bar--is-sticky': this.isAnchorBarSticky,
        }}>
        <div
          class={{
            'dc-ui-anchor-bar__inner': true,
            'dc-ui-anchor-bar__inner--has-overflow': this.hasOverflow,
          }}
          ref={(el) => (this.anchorBarContainer = el)}>
          {this.hasOverflow && overflowButtonLeft}
          <nav class="dc-ui-anchor-bar__inner-wrapper">
            <slot></slot>
          </nav>
          {this.hasOverflow && overflowButtonRight}
        </div>
      </div>
    );
  }
}
